// The standalone metronome (#413): a practice tool with no tune.
//
// It is drawn in the app's own vocabulary, not a page of its own: Play and
// Stop are the shared transport bar at the foot of the screen (its status
// strip carries the bar count and the timer), the tempo is the Tempo panel's
// stepper, the drum loop is the band's picker and rate line, the trainers sit
// in the practice panel's grid, and every row is a settings row. The beat
// lights are the one thing only this page draws.
//
// It drives the SAME scheduler as the band — an empty form with the click on —
// so the click's voice, accents, subdivision, fader and routing are the band's
// metronome channel, and a drum loop plays exactly as it does behind a tune.
// The ramp is the scheduler's per-pass tempo: one pass of the practice form is
// one ramp block. Nothing here touches the loaded tune, and pressing Start is
// the one thing that takes the shared player over; leaving the page stops what
// the page started (and only that), so the workbench never inherits a click
// track as if it were its own band.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getChordPlayer, type ArrangementOptions, type TransportState } from '@/audio/player';
import { drumLoopURL, loopsForMeter, type DrumLoop } from '@/audio/drumLoops';
import { useDrumLoops } from '@/data/uploadedLoops';
import {
  clockText,
  gapClickSounds,
  METRE_CHOICES,
  practiceFormBars,
  rampTempoForBlock,
  SESSION_CHOICES,
  type PracticeMetronome,
} from '@/audio/practiceMetronome';
import { usePlaybackBeat, usePlaybackPass } from '@/audio/usePlaybackBeat';
import { TEMPO_MAX, TEMPO_MIN, useStore } from '@/state/store';
import { NumberField } from '@/components/NumberField';
import { LevelFader, SegmentedChoice } from '@/components/MixerControls';
import {
  DrumLoopMenu,
  DrumLoopRate,
  DrumTimeStretch,
  FormField,
  TempoStepper,
} from '@/components/PracticeControls';
import { TransportBar } from '@/components/TransportBar';
import {
  CLICK_ACCENT_CHOICES,
  CLICK_ROUTING_CHOICES,
  CLICK_SOUND_CHOICES,
  CLICK_SUBDIVISION_CHOICES,
} from '@/components/clickChoices';
import './MetronomePage.css';

const clampTempo = (n: number) => Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(n)));

/** The player options for these settings. */
function practiceOptions(
  settings: PracticeMetronome,
  loop: DrumLoop | null,
  drumTimeStretch: boolean,
): ArrangementOptions {
  const beatsPerBar = settings.beatsPerBar;
  const loopBars = loop ? loop.beats / beatsPerBar : null;
  const formBars = practiceFormBars(settings, loopBars);
  const ramp = settings.ramp;
  return {
    bpm: ramp.enabled ? ramp.from : settings.bpm,
    bpmForPass: ramp.enabled ? (pass) => rampTempoForBlock(pass, ramp) : undefined,
    beatsPerBar,
    formBeats: formBars * beatsPerBar,
    // With no loop the click is the only thing keeping time, so it always sounds.
    metronome: settings.click || !loop,
    clickGap: settings.gap.enabled ? { on: settings.gap.on, off: settings.gap.off } : undefined,
    drums: loop
      ? { url: drumLoopURL(loop), bpm: loop.bpm, beats: loop.beats, seconds: loop.seconds }
      : null,
    drumTimeStretch,
    loop: true,
    nowPlaying: { title: 'Metronome' },
  };
}

export function MetronomePage() {
  const settings = useStore((s) => s.practiceMetronome);
  const patch = useStore((s) => s.patchPracticeMetronome);
  const click = useStore((s) => s.metronomeSettings);
  const patchClick = useStore((s) => s.patchMetronome);
  const drumTimeStretch = useStore((s) => s.drumTimeStretch);
  const audioEnabled = useStore((s) => s.audioEnabled);

  const loops = useDrumLoops();
  const meterLoops = useMemo(
    () => loopsForMeter(loops, settings.beatsPerBar),
    [loops, settings.beatsPerBar],
  );
  const loop = settings.drumLoopKey
    ? (meterLoops.find((l) => l.key === settings.drumLoopKey) ?? null)
    : null;
  const loopBars = loop ? loop.beats / settings.beatsPerBar : null;
  const formBars = practiceFormBars(settings, loopBars);

  // Ours = the transport is running AND it is the run this page started.
  const [transport, setTransport] = useState<TransportState>('idle');
  const ownerRef = useRef<number | null>(null);
  useEffect(() => getChordPlayer().onTransportChange(setTransport), []);
  const ours =
    transport !== 'idle' &&
    ownerRef.current !== null &&
    ownerRef.current === getChordPlayer().getPlaybackGeneration();

  const optionsRef = useRef<ArrangementOptions>(practiceOptions(settings, loop, drumTimeStretch));
  const options = useMemo(
    () => practiceOptions(settings, loop, drumTimeStretch),
    [settings, loop, drumTimeStretch],
  );
  optionsRef.current = options;

  // Seconds played this session; a pause does not count.
  const [played, setPlayed] = useState(0);

  const start = useCallback(async () => {
    const player = getChordPlayer();
    await player.playArrangement([], optionsRef.current);
    ownerRef.current = player.isSequencePlaying() ? player.getPlaybackGeneration() : null;
    setPlayed(0);
    // A transport already 'playing' (a restart) reports no change.
    setTransport(player.isSequencePlaying() ? 'playing' : 'idle');
  }, []);
  const stop = useCallback(() => {
    const player = getChordPlayer();
    if (ownerRef.current !== null && ownerRef.current === player.getPlaybackGeneration()) {
      player.stop();
    }
    ownerRef.current = null;
    setPlayed(0);
  }, []);

  // Leaving the page stops what the page started, and nothing else.
  useEffect(() => stop, [stop]);

  // Live changes: the form's length is snapshotted at Start, so a new metre or
  // form length restarts; everything else lands on the next beat.
  const shapeKey = `${settings.beatsPerBar}:${formBars}`;
  const shapeRef = useRef(shapeKey);
  useEffect(() => {
    if (!ours) {
      shapeRef.current = shapeKey;
      return;
    }
    if (shapeRef.current !== shapeKey) {
      shapeRef.current = shapeKey;
      void start();
      return;
    }
    getChordPlayer().setArrangementOptions(options);
  }, [ours, options, shapeKey, start]);

  // The session timer, counting only while the click plays.
  const counting = ours && transport === 'playing';
  useEffect(() => {
    if (!counting) return;
    let last = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      // Read the step now: the updater runs later, after `last` has moved on.
      const step = (now - last) / 1000;
      last = now;
      setPlayed((p) => p + step);
    }, 250);
    return () => window.clearInterval(timer);
  }, [counting]);
  const sessionSeconds = settings.sessionMinutes * 60;
  useEffect(() => {
    if (ours && sessionSeconds > 0 && played >= sessionSeconds) stop();
  }, [ours, played, sessionSeconds, stop]);

  // Where the click is: the bar, its beat, and whether this bar is a gap.
  const beat = usePlaybackBeat();
  const pass = usePlaybackPass();
  const bpb = settings.beatsPerBar;
  const beatInBar = ours && beat >= 0 ? Math.floor(beat) % bpb : -1;
  const bar = ours && beat >= 0 && pass > 0 ? (pass - 1) * formBars + Math.floor(beat / bpb) : -1;
  const silentBar = ours && settings.gap.enabled && bar >= 0 && !gapClickSounds(bar, settings.gap);
  const liveTempo = settings.ramp.enabled
    ? rampTempoForBlock(ours && pass > 0 ? pass : 1, settings.ramp)
    : settings.bpm;

  // The transport's status strip: the bar, a gap bar, and the clock.
  const status = ours
    ? [
        ...(bar >= 0 ? [`Bar ${bar + 1}`] : []),
        ...(silentBar ? ['Keep time'] : []),
        sessionSeconds > 0
          ? `${clockText(Math.max(0, sessionSeconds - played))} left`
          : clockText(played),
      ]
    : undefined;

  const setBpm = (bpm: number) => patch({ bpm: clampTempo(bpm) });
  const rampOn = settings.ramp.enabled;

  return (
    <div className="metronome-page has-transport-bar" data-testid="metronome-page">
      <div className="page-header">
        <h1>Metronome</h1>
      </div>

      <section className="panel tempo-panel transport-surface metronome-tempo" aria-label="Tempo">
        <p className="panel-bignum" data-testid="metronome-bpm">
          {liveTempo}
          <small>bpm</small>
        </p>
        <div
          className="metronome-beats"
          role="img"
          aria-label={beatInBar >= 0 ? `Beat ${beatInBar + 1} of ${bpb}` : `${bpb} beats`}
          data-silent={silentBar || undefined}
        >
          {Array.from({ length: bpb }, (_, i) => (
            <span
              key={i}
              className={`metronome-beat${i === beatInBar ? ' on' : ''}${i === 0 ? ' one' : ''}`}
            />
          ))}
        </div>
        <TempoStepper
          id="metronome-tempo"
          label="Tempo"
          tempo={settings.bpm}
          setTempo={setBpm}
          disabled={rampOn}
        />
      </section>

      <section className="transport-surface practice-section" aria-labelledby="metronome-click">
        <h2 className="practice-section-title" id="metronome-click">
          Click
        </h2>
        <div className="settings-row">
          <span className="settings-label">Beats per bar</span>
          <SegmentedChoice
            label="Beats per bar"
            value={String(bpb)}
            options={METRE_CHOICES.map((n) => ({ id: String(n), label: String(n) }))}
            onChange={(n) => patch({ beatsPerBar: Number(n) })}
            testid="metronome-metre"
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Sound</span>
          <SegmentedChoice
            label="Click sound"
            value={click.sound}
            options={CLICK_SOUND_CHOICES}
            onChange={(sound) => patchClick({ sound })}
            testid="click-sound"
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Beats</span>
          <SegmentedChoice
            label="Click beats"
            value={click.accents}
            options={CLICK_ACCENT_CHOICES}
            onChange={(accents) => patchClick({ accents })}
            testid="click-accents"
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Fill</span>
          <SegmentedChoice
            label="Click subdivision"
            value={click.subdivision}
            options={CLICK_SUBDIVISION_CHOICES}
            onChange={(subdivision) => patchClick({ subdivision })}
            testid="click-subdivision"
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Effects</span>
          <SegmentedChoice
            label="Click routing"
            value={click.routing}
            options={CLICK_ROUTING_CHOICES}
            onChange={(routing) => patchClick({ routing })}
            testid="click-routing"
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Level</span>
          <LevelFader fader="click" label="Click" />
        </div>
      </section>

      <section className="transport-surface practice-section" aria-labelledby="metronome-drums">
        <h2 className="practice-section-title" id="metronome-drums">
          Drums
        </h2>
        <div className="settings-row">
          <span className="settings-label">Loop</span>
          <DrumLoopMenu
            loops={meterLoops}
            value={loop?.key ?? null}
            onChange={(key) => patch({ drumLoopKey: key })}
            noneLabel="None"
            className="band-mixer-sound-select"
          />
        </div>
        {loop && (
          <>
            <DrumLoopRate
              loopBpm={loop.bpm}
              effectiveTempo={liveTempo}
              activePct={100}
              disabled={rampOn}
              setTempo={setBpm}
            />
            {/* The mixer's drum settings row: its switches side by side. */}
            <div className="mixer-controls">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.click}
                  onChange={(e) => patch({ click: e.target.checked })}
                  data-testid="metronome-click-with-loop"
                />
                Click with the loop
              </label>
              <DrumTimeStretch />
            </div>
          </>
        )}
      </section>

      <section className="transport-surface practice-section" aria-labelledby="metronome-practice">
        <h2 className="practice-section-title" id="metronome-practice">
          Practice
        </h2>
        <label className="switch practice-switch">
          <span>
            <strong>Gap click</strong>
          </span>
          <input
            type="checkbox"
            checked={settings.gap.enabled}
            onChange={(e) => patch({ gap: { ...settings.gap, enabled: e.target.checked } })}
            data-testid="metronome-gap"
          />
        </label>
        {settings.gap.enabled && (
          <div className="practice-grid">
            <FormField label="Bars on">
              <NumberField
                value={settings.gap.on}
                min={1}
                max={16}
                onCommit={(on) => patch({ gap: { ...settings.gap, on } })}
                data-testid="metronome-gap-on"
              />
            </FormField>
            <FormField label="Bars off">
              <NumberField
                value={settings.gap.off}
                min={1}
                max={16}
                onCommit={(off) => patch({ gap: { ...settings.gap, off } })}
                data-testid="metronome-gap-off"
              />
            </FormField>
          </div>
        )}
        <label className="switch practice-switch">
          <span>
            <strong>Speed trainer</strong>
          </span>
          <input
            type="checkbox"
            checked={rampOn}
            onChange={(e) => patch({ ramp: { ...settings.ramp, enabled: e.target.checked } })}
            data-testid="metronome-ramp"
          />
        </label>
        {rampOn && (
          <>
            <div className="practice-grid">
              <FormField label="From">
                <NumberField
                  value={settings.ramp.from}
                  min={TEMPO_MIN}
                  max={TEMPO_MAX}
                  onCommit={(from) => patch({ ramp: { ...settings.ramp, from } })}
                  data-testid="metronome-ramp-from"
                />
              </FormField>
              <FormField label="To">
                <NumberField
                  value={settings.ramp.to}
                  min={TEMPO_MIN}
                  max={TEMPO_MAX}
                  onCommit={(to) => patch({ ramp: { ...settings.ramp, to } })}
                  data-testid="metronome-ramp-to"
                />
              </FormField>
              <FormField label="Step">
                <NumberField
                  value={settings.ramp.step}
                  min={1}
                  max={40}
                  onCommit={(step) => patch({ ramp: { ...settings.ramp, step } })}
                  data-testid="metronome-ramp-step"
                />
              </FormField>
              <FormField label="Bars/step">
                <NumberField
                  value={settings.ramp.bars}
                  min={1}
                  max={32}
                  onCommit={(bars) => patch({ ramp: { ...settings.ramp, bars } })}
                  data-testid="metronome-ramp-bars"
                />
              </FormField>
            </div>
            {formBars !== settings.ramp.bars && (
              <p className="panel-hint" data-testid="metronome-ramp-note">
                Steps every {formBars} bars.
              </p>
            )}
          </>
        )}
        <div className="settings-row">
          <span className="settings-label">Minutes</span>
          <SegmentedChoice
            label="Session length"
            value={String(settings.sessionMinutes)}
            options={SESSION_CHOICES.map((m) => ({
              id: String(m),
              label: m === 0 ? 'Off' : String(m),
            }))}
            onChange={(m) => patch({ sessionMinutes: Number(m) })}
            testid="metronome-session"
          />
        </div>
      </section>

      <TransportBar
        transport={ours ? transport : 'idle'}
        audioEnabled={audioEnabled}
        onPlay={() => void start()}
        onStop={stop}
        status={status}
        panels={[]}
      />
    </div>
  );
}
